import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { apiUploadsS3 } from 'api/upload';
import {
  useMutationQuestionImportFile,
  useMutationQuestionImportAnki,
} from 'gql/mutations/question';
import extract from 'helpers/extract';
import { trackEvent, reportError } from 'lib/tracking';

import QuestionImportFile from './QuestionImportFile';

const QuestionImportFileContainer = ({ course, handleModalClose }) => {
  const { t } = useTranslation();
  const [ankiSettings, setAnkiSettings] = useState(null);
  const mutationQuestionImportFile = useMutationQuestionImportFile();
  const mutationQuestionImportAnki = useMutationQuestionImportAnki();
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        chapterId: yup
          .string()
          .trim()
          .matches(
            /^|([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i,
          ),
        file: yup
          .mixed()
          .required(
            t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportFile/file-error-required',
            ),
          ),
      }),
    [t],
  );
  const handleQuestionsImport = useCallback(
    async (values, form) => {
      try {
        form.setStatus(null);
        const { file, ...variables } = extract(values, validationSchema);
        const extension = file.name.split('.').slice(-1)[0];
        if (extension === 'apkg') {
          const filePath = await apiUploadsS3(file);
          const {
            data: { questionImportAnki },
          } = await mutationQuestionImportAnki({
            fileName: file.name,
            filePath,
          });
          setAnkiSettings({
            file,
            variables,
            ...questionImportAnki,
          });
        } else if (
          [
            'ppt',
            'pps',
            'pot',
            'pptx',
            'ppsx',
            'potx',
            'odp',
            'key',
            'pdf',
          ].includes(extension)
        ) {
          const filePath = await apiUploadsS3(file);
          const {
            data: { questionImportFile },
          } = await mutationQuestionImportFile({
            ...variables,
            courseId: course.id,
            extension,
            fileName: file.name,
            filePath,
          });
          handleModalClose(questionImportFile);
        } else {
          const {
            data: { questionImportFile },
          } = await mutationQuestionImportFile({
            ...variables,
            courseId: course.id,
            extension,
            fileName: file.name,
            file,
          });
          handleModalClose(questionImportFile);
        }
        trackEvent('question', 'import-file');
      } catch (err) {
        form.setStatus('500');
        reportError('question', 'import-file', err);
      }
    },
    [
      course,
      handleModalClose,
      mutationQuestionImportFile,
      mutationQuestionImportAnki,
      validationSchema,
    ],
  );
  const handleQuestionsImportAnki = useCallback(
    async (values, form) => {
      try {
        form.setStatus(null);
        const extension = ankiSettings.file.name.split('.').slice(-1)[0];
        const filePath = await apiUploadsS3(ankiSettings.file);
        const {
          data: { questionImportFile },
        } = await mutationQuestionImportFile({
          ...ankiSettings.variables,
          courseId: course.id,
          extension,
          fileName: ankiSettings.file.name,
          filePath,
          ...values,
        });
        handleModalClose(questionImportFile);
        trackEvent('question', 'import-file');
      } catch (err) {
        form.setStatus('500');
        reportError('question', 'import-file', err);
      }
    },
    [ankiSettings, course, handleModalClose, mutationQuestionImportFile],
  );
  return (
    <QuestionImportFile
      ankiSettings={ankiSettings}
      chapters={course.chapters}
      handleQuestionsImport={handleQuestionsImport}
      handleQuestionsImportAnki={handleQuestionsImportAnki}
      validationSchema={validationSchema}
      t={t}
    />
  );
};

QuestionImportFileContainer.propTypes = {
  course: PropTypes.object.isRequired,
  handleModalClose: PropTypes.func.isRequired,
};

export default QuestionImportFileContainer;
